import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { imgSrc, priceLabel, soldOut } from "../../data/artworks";
import { useSettings } from "../../lib/settings";
import { useT } from "../../lib/content";
import { Reveal, Eyebrow, MaskLines, EASE } from "./Reveal";

const match = (k) => (a) => (k === "tous" ? true : k === "vendu" ? a.vendu : a.cat === k);

const Card = ({ a, i, onOpen, big }) => {
  const spot = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
  };
  return (
    <motion.button layout type="button" onClick={() => onOpen(a)} onMouseMove={spot} data-testid={`artwork-card-${a.id}`}
      initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.9, ease: EASE, delay: (i % 3) * 0.08 }}
      className={`group text-left spot-card ${big ? "sm:col-span-2 lg:col-span-1 lg:row-span-2" : ""}`}>
      <div className={`relative overflow-hidden bg-[#E9E2D6] ${big ? "aspect-[4/5] lg:aspect-auto lg:h-full" : "aspect-[4/5]"}`}>
        <img src={imgSrc(a.img)} alt={a.alt || a.t} loading="lazy"
          className="absolute inset-0 w-full h-full object-contain p-5 sm:p-7 drop-shadow-[0_18px_22px_rgba(21,32,43,0.28)] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" />
        <span className="spot-layer" />
        <span className={`absolute top-4 left-4 backdrop-blur px-3 py-1 text-[10px] tracking-[0.22em] uppercase ${soldOut(a) ? "bg-[#15202B] text-[#F6F1E9]" : "bg-[#F6F1E9]/90 text-[#15202B]"}`}>{a.vendu ? "Vendue" : soldOut(a) ? "Épuisé" : a.unique === false || a.cat === "autre" ? "Produit" : "Original"}</span>
        <span className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-[#F6F1E9] grid place-items-center translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-500">
          <ArrowUpRight className="w-5 h-5 text-[#15202B]" />
        </span>
      </div>
      <div className="pt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="f-display text-2xl sm:text-[1.7rem] leading-tight text-[#15202B]">{a.t}</h3>
          <p className="text-sm text-[#55636F] mt-1">{a.tech}{a.dim ? ` · ${a.dim}` : ""}</p>
        </div>
        <span className={`shrink-0 pt-1 ${a.prix && !soldOut(a) ? "text-lg text-[#15202B] tabular-nums" : "text-xs tracking-[0.14em] uppercase text-[#8A6420] pt-2"}`}>{priceLabel(a)}</span>
      </div>
    </motion.button>
  );
};

export const Gallery = ({ items, onOpen }) => {
  const [f, setF] = useState("tous");
  const list = useMemo(() => items.filter(match(f)), [f, items]);
  const { settings } = useSettings();
  const t = useT();
  const all = [["tous", "Toutes les œuvres"], ...settings.categories.map((c) => [c.key, c.label]), ["vendu", "Vendues"]];
  const filters = all.filter(([k]) => k !== "vendu" || items.some(match(k)));

  return (
    <section id="galerie" className="relative py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12 sm:mb-16">
          <div className="lg:col-span-7">
            <Eyebrow>{t("gallery_eyebrow")}</Eyebrow>
            <h2 className="f-display mt-5 text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.98] text-[#15202B]">
              <MaskLines inView lines={[t("gallery_title1"), <em key="e">{t("gallery_title2")}</em>]} />
            </h2>
          </div>
          <Reveal className="lg:col-span-5 text-[#3B4A57] text-base sm:text-lg font-light">
            {t("gallery_intro")}
          </Reveal>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-10 -mx-5 px-5 sm:mx-0 sm:px-0 no-scrollbar" role="group" aria-label="Filtrer les œuvres">
          {filters.map(([k, l]) => (
            <button key={k} onClick={() => setF(k)} data-testid={`gallery-filter-${k}`} aria-pressed={f === k}
              className={`relative shrink-0 px-5 py-2.5 rounded-full text-sm border transition-colors duration-300 ${f === k ? "text-[#F6F1E9] border-[#15202B]" : "text-[#15202B] border-[#15202B]/20 hover:border-[#15202B]/60"}`}>
              {f === k && <motion.span layoutId="chip" className="absolute inset-0 rounded-full bg-[#15202B]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
              <span className="relative">{l} <span className="opacity-60 ml-1">{items.filter(match(k)).length}</span></span>
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 lg:gap-x-8 gap-y-12 lg:auto-rows-auto">
          {list.map((a, i) => <Card key={a.id} a={a} i={i} onOpen={onOpen} />)}
          {list.length === 0 && (
            <div className="sm:col-span-2 lg:col-span-3 bg-[#EFE8DC] py-16 px-6 text-center" data-testid="gallery-empty-state">
              <p className="f-display text-3xl text-[#15202B]">Bientôt disponible</p>
              <p className="mt-2 text-[#3B4A57] font-light">Les créations de cette collection arrivent prochainement.</p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
